import { useState, useRef, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import useLabelingStore from '../store/useDetectionStore.js';
import { autoDetectAPI, autoDetectFromMasterAPI } from '../api/detectionLabelApi.js';
import { getToken } from '../../../../../utils/themeTokens.js';
import { useI18n } from '../../../../../i18n';
import {
  buildAiAnnotations,
  getImageBlob,
  isAbortError,
  isUnlabeled,
} from '../utils/aiSuggestions.js';

const UPLOAD_DEBOUNCE_MS = 700;    // wait for a whole upload batch to land in the store

/**
 * Detection probes for one class = its name plus any comma-separated aiAliases,
 * lowercased and de-duped. These are the natural-language terms the open-vocab
 * detector is told to look for so a class like "Worker" can map to "person".
 */
const classProbes = (cls) => {
  const raw = [cls.name, ...String(cls.aiAliases || '').split(',')];
  const seen = new Set();
  const out = [];
  raw.forEach((t) => {
    const term = String(t || '').trim().toLowerCase();
    if (term && !seen.has(term)) { seen.add(term); out.push(term); }
  });
  return out;
};

/**
 * Build the payload for "My classes only" detection:
 *  - probes: flat, de-duped list of terms sent to the open-vocab detector.
 *  - probeToClassId: term(lowercase) -> user classId, so every returned box maps
 *    back to the class that owns that term. First class to claim a term wins.
 */
const buildClassMapping = (classes) => {
  const probeToClassId = new Map();
  const probes = [];
  classes.forEach((cls) => {
    classProbes(cls).forEach((term) => {
      if (!probeToClassId.has(term)) {
        probeToClassId.set(term, String(cls.id));
        probes.push(term);
      }
    });
  });
  return { probes, probeToClassId };
};

/**
 * AI auto-labeling engine for the detection page.
 * Runs YOLO/YOLO-World via the training API, maps detected class names onto the
 * project's class list (creating missing classes), converts pixel xyxy boxes to
 * the page's normalized center format, and injects them as editable AI suggestions.
 *
 * Automatic triggers:
 *  - image upload  -> auto-labels the uploaded batch (asks once if auto mode is off)
 *  - navigation    -> auto-labels the opened image (auto mode + toggle on)
 *
 * Cancellation: every run takes a monotonic run id. Stop bumps the id, aborts the
 * in-flight HTTP request and latches `stopRequestedRef`, so neither the loop nor
 * the navigation trigger can restart on their own and results from a superseded
 * run are dropped instead of applied.
 */
const useAutoLabel = (saveState) => {
  const { t } = useI18n();
  const [isDetecting, setIsDetecting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // {done, total, found, currentName}

  const stopRequestedRef = useRef(false);   // latched by Stop until the next explicit start
  const abortRef = useRef(null);            // AbortController of the in-flight request
  const runIdRef = useRef(0);               // monotonic run id; stale results are ignored
  const attemptedRef = useRef(new Set());   // image ids already auto-attempted
  const uploadHandledRef = useRef(new Set()); // uploaded image ids already processed/prompted
  const uploadTimerRef = useRef(null);
  const runningRef = useRef(false);
  const rawDetectionsRef = useRef(new Map()); // imageId -> raw response, for live re-filtering
  const masterBlobRef = useRef({ id: null, blob: null });

  const {
    autoMode, autoSettings, setAutoSettings, setAutoMode,
    currentImageIndex, images, masterImageId, setMasterImageId,
  } = useLabelingStore();

  // ── run lifecycle ───────────────────────────────────────────────────────────

  /** Start a new run: clears the stop latch and returns this run's id. */
  const beginRun = useCallback(() => {
    stopRequestedRef.current = false;
    setIsStopping(false);
    runIdRef.current += 1;
    abortRef.current = new AbortController();
    return runIdRef.current;
  }, []);

  /** True once a newer run started (or Stop bumped the id) — drop the result. */
  const isStale = useCallback((runId) => runIdRef.current !== runId, []);

  /**
   * Stop everything now: abort the in-flight inference, supersede the run id and
   * latch the stop so the navigation trigger cannot immediately restart it.
   * Boxes already written stay — Stop means "process no more", not "undo".
   */
  const stopAll = useCallback(() => {
    if (!runningRef.current) return;
    stopRequestedRef.current = true;
    setIsStopping(true);
    runIdRef.current += 1;                 // supersede: late results are ignored
    try { abortRef.current?.abort(); } catch { /* already settled */ }
    abortRef.current = null;
    // Auto-label-on-open would re-trigger the moment the loop released its lock,
    // which is why Stop used to look like a no-op. Switch it off visibly.
    if (useLabelingStore.getState().autoSettings.autoOnNavigate) {
      setAutoSettings({ autoOnNavigate: false });
    }
  }, [setAutoSettings]);

  // ── detection -> annotations ────────────────────────────────────────────────

  /**
   * Map raw detections to annotations.
   * @param {object} opts
   *   opts.strict          - "My classes only" / master mode: drop any box that
   *                          doesn't map to an existing class; never invent one.
   *   opts.probeToClassId  - term(lowercase) -> classId map (from aliases/master).
   *   opts.allowNewClasses - false when re-filtering already-returned detections,
   *                          so a re-filter never re-creates classes.
   * In non-strict (smart/coco) mode, unknown classes are still created as before.
   */
  const buildAnnotations = useCallback((detections, imageSize, existingAnns, opts = {}) => {
    const store = useLabelingStore.getState();
    const { anns, newClasses, skipped } = buildAiAnnotations(detections, imageSize, existingAnns, {
      ...opts,
      classes: store.classes,
    });
    if (newClasses.length > 0) {
      store.addAdditionalClasses(newClasses);
    }
    return { anns, newClassCount: newClasses.length, skipped };
  }, []);

  // ── master image ────────────────────────────────────────────────────────────

  /**
   * Resolve the master image into what the guided endpoint needs:
   * its manual boxes in MASTER-image pixels, each with its class name and the
   * text probes for that class, plus the master blob (cached across the batch).
   * Returns null when no master is set or it has no usable size.
   */
  const getMasterContext = useCallback(async () => {
    const store = useLabelingStore.getState();
    const id = store.masterImageId;
    if (!id) return null;
    const master = store.images.find((im) => im.id === id);
    if (!master) return null;

    const W = master.originalWidth || 0;
    const H = master.originalHeight || 0;
    if (!W || !H) return null;

    // Only the user's own boxes define the master — never AI suggestions.
    const manual = (master.annotations || []).filter((a) => !a.isAI);
    const boxes = [];
    const probeToClassId = new Map();
    const classNames = [];

    manual.forEach((a) => {
      const cls = store.classes.find((c) => String(c.id) === String(a.classId));
      if (!cls || !String(cls.name || '').trim()) return;   // unclassed box: ignored
      boxes.push({
        x1: (a.centerX - a.width / 2) * W,
        y1: (a.centerY - a.height / 2) * H,
        x2: (a.centerX + a.width / 2) * W,
        y2: (a.centerY + a.height / 2) * H,
        class: cls.name,
        probes: classProbes(cls),
      });
      probeToClassId.set(String(cls.name).trim().toLowerCase(), String(cls.id));
      if (!classNames.includes(cls.name)) classNames.push(cls.name);
    });

    const ctx = {
      master,
      boxes,
      probeToClassId,
      classNames,
      unclassed: manual.length - boxes.length,
      blob: null,
    };
    if (boxes.length === 0) return ctx;

    if (masterBlobRef.current.id !== id || !masterBlobRef.current.blob) {
      masterBlobRef.current = { id, blob: await getImageBlob(master) };
    }
    ctx.blob = masterBlobRef.current.blob;
    return ctx;
  }, []);

  // ── single-image inference ──────────────────────────────────────────────────

  /**
   * Detect + inject suggestions for one image index.
   * @param {number} imageIndex
   * @param {object} ctx  {runId, master} — master present => guided mode.
   * Returns {found, newClassCount, perClass, warnings}.
   */
  const autoLabelImage = useCallback(async (imageIndex, ctx = {}) => {
    const { runId, master } = ctx;
    const store = useLabelingStore.getState();
    const img = store.images[imageIndex];
    const empty = { found: 0, newClassCount: 0, perClass: {}, warnings: [], gateError: null };
    if (!img) return empty;

    const { confidence, iouThreshold, similarity } = store.autoSettings;
    const signal = abortRef.current?.signal;
    const blob = await getImageBlob(img);

    let response;
    let strict;
    let probeToClassId;

    if (master) {
      // Master-guided: only the classes drawn on the master are in play.
      strict = true;
      probeToClassId = master.probeToClassId;
      response = await autoDetectFromMasterAPI({
        blob,
        fileName: img.fileName,
        masterBlob: master.blob,
        masterFileName: master.master?.fileName,
        masterBoxes: master.boxes,
        confidence,
        iouThreshold,
        similarity,
        signal,
      });
    } else {
      let detectMode = store.autoSettings.detectMode;

      // "My classes only": map each class (name + aiAliases) to detector probes.
      // With no classes there is nothing to restrict to, so fall back to Smart.
      let probes = [];
      probeToClassId = new Map();
      if (detectMode === 'myclasses') {
        const mapping = buildClassMapping(store.classes);
        probes = mapping.probes;
        probeToClassId = mapping.probeToClassId;
        if (probes.length === 0) detectMode = 'smart';
      }

      strict = detectMode === 'myclasses';
      // In myclasses mode send the expanded probe list; other modes still send the
      // plain class names (used as extra vocabulary / naming hints).
      const classNames = strict ? probes : store.classes.map((c) => c.name).filter(Boolean);

      response = await autoDetectAPI({
        blob,
        fileName: img.fileName,
        mode: detectMode,
        confidence,
        iouThreshold,
        classNames,
        signal,
      });
    }

    // Drop the result if this run was stopped, or a newer run superseded it,
    // while the request was in flight. Both conditions matter: a plain stop with
    // no follow-up run is a straggler, not a supersede.
    if (stopRequestedRef.current || (runId != null && isStale(runId))) return empty;

    const payload = response?.data;
    if (!payload?.success) {
      throw new Error(payload?.message || 'Detection failed');
    }

    const detections = payload?.data?.detections || [];
    const imageSize = payload?.data?.image_size || {
      width: img.originalWidth,
      height: img.originalHeight,
    };

    // Keep the raw response so the confidence slider can re-filter these boxes
    // without another round of inference.
    rawDetectionsRef.current.set(img.id, {
      detections,
      imageSize,
      strict,
      probeToClassId,
      confidence,
    });

    /*
     * Apply only what clears the threshold the user set.
     *
     * The raw list above stays UNFILTERED on purpose — lowering the slider must
     * be able to restore boxes without another round of inference — but the
     * boxes that actually land had no filter at all, while the server
     * deliberately returns sub-threshold ones: /detect/everything runs its
     * YOLO-World stage at `max(confidence - 0.10, 0.05)`, and its FastSAM stage
     * reports a CLIP margin on an entirely different scale that is never
     * compared with the caller's confidence.
     *
     * Measured on one photo at the panel's default 0.25: 59 boxes applied, 34
     * of them below the threshold, two at exactly 0.000. Nudging the slider and
     * nudging it back then deleted a third of them — same threshold, same
     * detections, different result — which is the tell that one path filtered
     * and the other did not.
     */
    const kept = detections.filter((d) => (d.confidence ?? 1) >= confidence);

    const { anns, newClassCount } = buildAnnotations(
      kept,
      imageSize,
      (img.annotations || []),
      { strict, probeToClassId },
    );

    if (anns.length > 0) {
      if (imageIndex === useLabelingStore.getState().currentImageIndex) {
        saveState?.(); // allow Ctrl+Z to undo the injection on the visible image
      }
      useLabelingStore.getState().applyAiAnnotations(imageIndex, anns);
    }
    return {
      found: anns.length,
      newClassCount,
      perClass: payload?.data?.per_class_counts || {},
      warnings: payload?.data?.warnings || [],
      // Non-null when the master-image appearance gate could not run.
      gateError: payload?.data?.similarity_gate_error || null,
    };
  }, [buildAnnotations, saveState, isStale]);

  // ── batch runner ────────────────────────────────────────────────────────────

  /**
   * Sequential, cancellable batch over image IDs (indexes are resolved fresh
   * each step because injections replace the images array).
   * `master` (optional) switches every image to master-guided detection.
   */
  const runBatch = useCallback(async (imageIds, master = null) => {
    if (runningRef.current || imageIds.length === 0) return;
    runningRef.current = true;
    const runId = beginRun();
    setIsDetecting(true);

    let done = 0, found = 0, newClasses = 0, failed = 0;
    let gateError = null;      // appearance gate unavailable on the server
    const perClassTotals = {};
    if (master) master.classNames.forEach((name) => { perClassTotals[name] = 0; });
    setBatchProgress({ done: 0, total: imageIds.length, found: 0, currentName: '' });

    for (const id of imageIds) {
      if (stopRequestedRef.current || isStale(runId)) break;
      const imgs = useLabelingStore.getState().images;
      const index = imgs.findIndex((im) => im.id === id);
      if (index === -1) { done += 1; continue; } // image removed meanwhile
      const img = imgs[index];
      attemptedRef.current.add(id);
      if (!isUnlabeled(img)) { done += 1; continue; }

      setBatchProgress({ done, total: imageIds.length, found, currentName: img.fileName });
      try {
        const result = await autoLabelImage(index, { runId, master });
        found += result.found;
        newClasses += result.newClassCount;
        Object.entries(result.perClass).forEach(([name, count]) => {
          perClassTotals[name] = (perClassTotals[name] || 0) + count;
        });
        if (!gateError && result.gateError) gateError = result.gateError;
      } catch (err) {
        if (isAbortError(err)) break;          // Stop: not a failure
        console.error(`Auto-label failed for ${img.fileName}:`, err);
        failed += 1;
      }
      done += 1;
    }

    const stopped = stopRequestedRef.current;
    abortRef.current = null;
    setBatchProgress(null);
    setIsDetecting(false);
    setIsStopping(false);
    runningRef.current = false;

    // Master classes that never matched anywhere — warn instead of silently
    // labeling nothing.
    const missing = master
      ? master.classNames.filter((name) => !perClassTotals[name])
      : [];

    const destructive = getToken('--destructive', '#dc2626');
    // A missing appearance gate means master-image mode fell back to class-only
    // matching — never let that pass silently, and never auto-dismiss it.
    const mustAcknowledge = failed > 0 || missing.length > 0 || !!gateError;
    Swal.fire({
      icon: failed > 0 || gateError ? 'warning' : 'success',
      title: stopped ? t('aiLabel.result.stopped') : t('aiLabel.result.complete'),
      html: t('aiLabel.result.summary', { done, found }) +
        (newClasses > 0 ? `<br/>${t('aiLabel.result.newClasses', { count: newClasses })}` : '') +
        (failed > 0 ? `<br/><span style="color:${destructive}">${t('aiLabel.result.failed', { count: failed })}</span>` : '') +
        (gateError ? `<br/><span style="color:${destructive}">${t('aiLabel.result.gateOff', { reason: gateError })}</span>` : '') +
        (missing.length > 0 ? `<br/><small>${t('aiLabel.result.notFoundClasses', { list: missing.join(', ') })}</small>` : '') +
        (stopped ? `<br/><small>${t('aiLabel.result.navOff')}</small>` : '') +
        `<br/><small>${t('aiLabel.result.reviewNote')}</small>`,
      timer: mustAcknowledge ? undefined : 4000,
      showConfirmButton: mustAcknowledge,
    });
  }, [autoLabelImage, beginRun, isStale, t]);

  // ── panel actions ───────────────────────────────────────────────────────────

  /** Auto-label the currently visible image (panel button). */
  const detectCurrentImage = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    const runId = beginRun();
    setIsDetecting(true);
    try {
      const store = useLabelingStore.getState();
      const idx = store.currentImageIndex;
      // Honour master mode here too, unless the visible image IS the master.
      let master = null;
      if (store.autoSettings.useMaster) {
        const ctx = await getMasterContext();
        if (ctx?.boxes.length && ctx.master.id !== store.images[idx]?.id) master = ctx;
      }
      const { found, gateError } = await autoLabelImage(idx, { runId, master });
      if (gateError && !stopRequestedRef.current && !isStale(runId)) {
        Swal.fire({
          icon: 'warning',
          title: t('aiLabel.result.gateOffTitle'),
          html: t('aiLabel.result.gateOff', { reason: gateError }),
        });
      } else if (found === 0 && !stopRequestedRef.current && !isStale(runId)) {
        Swal.fire({
          toast: true, position: 'top-end', timer: 2500, showConfirmButton: false,
          icon: 'info', title: t('aiLabel.result.noObjects'),
        });
      }
    } catch (err) {
      if (!isAbortError(err)) {
        console.error('Auto-detect failed:', err);
        Swal.fire(
          t('aiLabel.result.detectFailed'),
          err?.response?.data?.message || err.message || t('aiLabel.result.unknownError'),
          'error',
        );
      }
    } finally {
      abortRef.current = null;
      runningRef.current = false;
      setIsDetecting(false);
      setIsStopping(false);
    }
  }, [autoLabelImage, beginRun, isStale, getMasterContext, t]);

  /** Auto-label every image that has no annotations yet (panel button). */
  const autoLabelAllUnlabeled = useCallback(async () => {
    const store = useLabelingStore.getState();
    const ids = store.images.filter(isUnlabeled).map((img) => img.id);
    if (ids.length === 0) {
      Swal.fire({
        toast: true, position: 'top-end', timer: 2500, showConfirmButton: false,
        icon: 'info', title: t('aiLabel.result.allLabeled'),
      });
      return;
    }
    // Master mode: keep the master out of the batch and restrict to its classes.
    let master = null;
    if (store.autoSettings.useMaster) {
      master = await getMasterContext();
      if (master?.boxes.length) {
        const targets = ids.filter((id) => id !== master.master.id);
        await runBatch(targets, master);
        return;
      }
    }
    await runBatch(ids);
  }, [runBatch, getMasterContext, t]);

  /**
   * Master-guided auto-label: label the remaining unlabeled images using only
   * the object types drawn on the master image.
   */
  const autoLabelFromMaster = useCallback(async () => {
    if (runningRef.current) return;
    const store = useLabelingStore.getState();
    if (!store.masterImageId) {
      Swal.fire(t('aiLabel.master.missingTitle'), t('aiLabel.master.missingText'), 'info');
      return;
    }
    const master = await getMasterContext();
    if (!master || master.boxes.length === 0) {
      Swal.fire(t('aiLabel.master.emptyTitle'), t('aiLabel.master.emptyText'), 'info');
      return;
    }
    if (master.unclassed > 0) {
      await Swal.fire({
        icon: 'warning',
        title: t('aiLabel.master.unclassedTitle'),
        text: t('aiLabel.master.unclassedText', { count: master.unclassed }),
      });
    }
    const targets = store.images
      .filter((img) => img.id !== master.master.id && isUnlabeled(img))
      .map((img) => img.id);
    if (targets.length === 0) {
      Swal.fire(t('aiLabel.master.aloneTitle'), t('aiLabel.master.aloneText'), 'info');
      return;
    }
    await runBatch(targets, master);
  }, [runBatch, getMasterContext, t]);

  /** Mark the currently visible image as the master reference. */
  const setCurrentAsMaster = useCallback(() => {
    const store = useLabelingStore.getState();
    const img = store.images[store.currentImageIndex];
    if (!img) return;
    masterBlobRef.current = { id: null, blob: null };   // drop the cached blob
    setMasterImageId(img.id);
    setAutoSettings({ useMaster: true });
  }, [setMasterImageId, setAutoSettings]);

  const clearMaster = useCallback(() => {
    masterBlobRef.current = { id: null, blob: null };
    setMasterImageId(null);
  }, [setMasterImageId]);

  // ── live threshold re-filter ────────────────────────────────────────────────

  /**
   * Re-filter the suggestions already on the visible image against a new
   * confidence, reusing the retained raw detections — no re-inference. Boxes are
   * restored when the threshold is lowered, down to the floor the run requested.
   */
  const refilterCurrentImage = useCallback((conf) => {
    const store = useLabelingStore.getState();
    const idx = store.currentImageIndex;
    const img = store.images[idx];
    if (!img) return;
    const raw = rawDetectionsRef.current.get(img.id);
    if (!raw) return;

    const kept = raw.detections.filter((d) => (d.confidence ?? 1) >= conf);
    const manual = (img.annotations || []).filter((a) => !a.isAI);
    const { anns } = buildAnnotations(kept, raw.imageSize, manual, {
      strict: raw.strict,
      probeToClassId: raw.probeToClassId,
      allowNewClasses: false,
    });
    store.setAiAnnotations(idx, anns);
  }, [buildAnnotations]);

  // Confidence changed -> re-filter the visible image's suggestions in place.
  const lastConfRef = useRef(autoSettings.confidence);
  useEffect(() => {
    const conf = autoSettings.confidence;
    if (conf === lastConfRef.current) return;
    lastConfRef.current = conf;
    if (runningRef.current) return;    // the running batch already uses the new value
    refilterCurrentImage(conf);
  }, [autoSettings.confidence, refilterCurrentImage]);

  // ── automatic triggers ──────────────────────────────────────────────────────

  /** Handle freshly uploaded images: auto-label them (ask once if auto mode is off). */
  const processUploads = useCallback(async () => {
    // No stop latch here: a fresh upload is a new user action, and images from a
    // stopped batch are already in uploadHandledRef, so nothing restarts.
    if (runningRef.current) {
      // busy with another detection — retry shortly instead of dropping the batch
      uploadTimerRef.current = setTimeout(processUploads, 1000);
      return;
    }
    const store = useLabelingStore.getState();
    const pending = store.images.filter(
      (img) => img.file && isUnlabeled(img) &&
        !attemptedRef.current.has(img.id) && !uploadHandledRef.current.has(img.id)
    );
    if (pending.length === 0) return;

    const ids = pending.map((img) => img.id);
    ids.forEach((id) => uploadHandledRef.current.add(id));

    // Master mode: never auto-label the master itself, and stay on its classes.
    let master = null;
    if (store.autoSettings.useMaster) {
      const ctx = await getMasterContext();
      if (ctx?.boxes.length) master = ctx;
    }
    const targets = master ? ids.filter((id) => id !== master.master.id) : ids;
    if (targets.length === 0) return;

    if (store.autoMode) {
      runBatch(targets, master);
      return;
    }

    const result = await Swal.fire({
      title: t('aiLabel.upload.title'),
      html: t('aiLabel.upload.body', { count: targets.length }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('aiLabel.upload.confirm'),
      cancelButtonText: t('aiLabel.upload.cancel'),
      confirmButtonColor: getToken('--primary', '#059669'),
    });
    if (result.isConfirmed) {
      useLabelingStore.getState().setAutoMode(true); // open the AI panel for review controls
      runBatch(targets, master);
    }
  }, [runBatch, getMasterContext, t]);

  // Watch for new uploads (local images carry a File; dataset-loaded ones don't)
  useEffect(() => {
    const hasFresh = images.some(
      (img) => img.file && isUnlabeled(img) &&
        !attemptedRef.current.has(img.id) && !uploadHandledRef.current.has(img.id)
    );
    if (!hasFresh) return;
    if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current);
    uploadTimerRef.current = setTimeout(processUploads, UPLOAD_DEBOUNCE_MS);
  }, [images, processUploads]);

  useEffect(() => () => {
    if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current);
    try { abortRef.current?.abort(); } catch { /* nothing in flight */ }
  }, []);

  // Auto-label on navigation: when auto mode + toggle are on, label unlabeled images as the user opens them.
  useEffect(() => {
    if (!autoMode || !autoSettings.autoOnNavigate) return;
    if (runningRef.current || stopRequestedRef.current) return;   // Stop latch
    const img = images[currentImageIndex];
    if (!img || attemptedRef.current.has(img.id)) return;
    if (!isUnlabeled(img)) return;
    if (autoSettings.useMaster && img.id === masterImageId) return; // never touch the master

    attemptedRef.current.add(img.id);
    runningRef.current = true;
    const runId = beginRun();
    setIsDetecting(true);
    (async () => {
      let master = null;
      if (autoSettings.useMaster) {
        const ctx = await getMasterContext();
        if (ctx?.boxes.length) master = ctx;
      }
      return autoLabelImage(currentImageIndex, { runId, master });
    })()
      .catch((err) => {
        if (!isAbortError(err)) console.error('Auto-label on navigate failed:', err);
      })
      .finally(() => {
        abortRef.current = null;
        runningRef.current = false;
        setIsDetecting(false);
        setIsStopping(false);
      });
  }, [
    autoMode, autoSettings.autoOnNavigate, autoSettings.useMaster, masterImageId,
    currentImageIndex, images, autoLabelImage, beginRun, getMasterContext,
  ]);

  // Auto mode switched off -> allow re-attempts later
  useEffect(() => {
    if (!autoMode) attemptedRef.current = new Set();
  }, [autoMode]);

  // Turning the navigation toggle back on is an explicit restart -> clear the latch.
  useEffect(() => {
    if (autoSettings.autoOnNavigate) stopRequestedRef.current = false;
  }, [autoSettings.autoOnNavigate]);

  return {
    autoMode,
    setAutoMode,
    autoSettings,
    setAutoSettings,
    isDetecting,
    isStopping,
    batchProgress,
    detectCurrentImage,
    autoLabelAllUnlabeled,
    autoLabelFromMaster,
    setCurrentAsMaster,
    clearMaster,
    masterImageId,
    stopAll,
  };
};

export default useAutoLabel;
